import React, { useContext } from 'react'
import { Box, Divider, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import { Image, type ImageProps } from '../Image'
import { spacingSelector } from '../../theme/selectors'
import { SectionContext } from '../Section/Context'
import { TESTIMONIAL_BACKGROUND_URL } from '../../cloudinary/assets'

interface CardListCard {
  id: string
  title: string
  listItems: string[]
  icon: Omit<ImageProps, 'sizes'>
}

export interface SectionContentCardListProps {
  heading: React.ReactNode
  cards: CardListCard[]
}

const Cards = styled('div')`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  flex-direction: row;
  flex-wrap: wrap;
  gap: ${spacingSelector(10)};
  width: 100%;
  ${({ theme }) => theme.breakpoints.down('lg')} {
    gap: ${spacingSelector(6)};
  }

  ${({ theme }) => theme.breakpoints.down('md')} {
    grid-template-columns: 1fr;
    gap: ${spacingSelector(4)};
  }
`

const CardListItem: React.FC<CardListCard> = ({ title, listItems, icon }) => (
  <Box sx={{ paddingBottom: 2, paddingRight: 2 }}>
    <Box sx={{ position: 'relative', height: '100%' }}>
      <Box
        sx={{
          position: 'absolute',
          top: { xs: '-20px', md: '-40px' },
          right: { xs: '-20px', md: '-40px' },
          rotate: '15deg',
          height: '80px',
          width: '80px',
          '& img': { display: 'block', width: '100%', height: '100%' },
        }}
      >
        <Image {...icon} sizes="80px" />
      </Box>
      <Box
        sx={{
          position: 'absolute',
          top: '12px',
          left: '12px',
          height: '100%',
          width: '100%',
          zIndex: -1,
          backgroundImage: `url(${TESTIMONIAL_BACKGROUND_URL})`,
          backgroundPosition: '0 0',
          backgroundSize: 'auto',
        }}
      />
      <Box
        sx={(theme) => ({
          background: theme.palette.background.default,
          border: `1px solid ${theme.palette.common.black}`,
          padding: 2.5,
          height: '100%',
        })}
      >
        <Typography variant="h3">{title}</Typography>
        <Divider
          sx={(theme) => ({
            borderColor: theme.palette.common.black,
            marginY: 2,
          })}
        />
        <Box component="ul" sx={{ listStyleType: 'none', padding: 0 }}>
          {listItems?.map((item) => (
            <Typography variant="body2" key={item} component="li">
              {item}
            </Typography>
          ))}
        </Box>
      </Box>
    </Box>
  </Box>
)

export const SectionContentCardList: React.FC<SectionContentCardListProps> = ({
  heading,
  cards,
}) => {
  const { theme } = useContext(SectionContext)
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {heading && (
        <Typography
          component="h2"
          variant="h2"
          align="center"
          sx={{
            color: theme === 'dark' ? 'primary.contrastText' : 'textPrimary',
          }}
        >
          {heading}
        </Typography>
      )}
      <Cards>
        {cards.map((card) => (
          <CardListItem key={card.id} {...card} />
        ))}
      </Cards>
    </Box>
  )
}
